import type { Ref, TextareaHTMLAttributes } from 'react';
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { cx } from '@/lib/cx';

/** Derived from the handler itself, so it cannot drift from React's own types. */
type TextareaInputEvent = Parameters<
  NonNullable<TextareaHTMLAttributes<HTMLTextAreaElement>['onInput']>
>[0];

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  ref?: Ref<HTMLTextAreaElement>;
  invalid?: boolean;
  /**
   * Grow to fit the content instead of scrolling, between these row counts.
   * Omitted = today's fixed-rows behaviour, unchanged.
   */
  autoGrow?: { minRows: number; maxRows: number };
}

export function Textarea({
  invalid,
  className,
  rows,
  autoGrow,
  ref,
  onInput,
  ...rest
}: TextareaProps) {
  const el = useRef<HTMLTextAreaElement | null>(null);
  // Only ever clear a height we ourselves wrote.
  const sized = useRef(false);

  // Depend on the numbers, never on the object: a call site passing an inline
  // `{ minRows: 1, maxRows: 6 }` gives a fresh identity on every render.
  const minRows = autoGrow?.minRows;
  const maxRows = autoGrow?.maxRows;

  const resize = useCallback(() => {
    const node = el.current;
    if (!node) return;

    if (minRows === undefined || maxRows === undefined) {
      if (sized.current) {
        node.style.height = '';
        node.style.overflowY = '';
        sized.current = false;
      }
      return;
    }

    const cs = window.getComputedStyle(node);
    const fontSize = parseFloat(cs.fontSize);
    const parsedLine = parseFloat(cs.lineHeight);
    // `line-height: normal` parses to NaN, so fall back rather than sizing to 0.
    const lineHeight = Number.isFinite(parsedLine)
      ? parsedLine
      : Number.isFinite(fontSize)
        ? fontSize * 1.5
        : 20;
    const paddingY = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
    const borderY =
      (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0);
    // `scrollHeight` counts padding but not border, so the two box models need
    // opposite corrections to land on the same visible height.
    const borderBox = cs.boxSizing === 'border-box';
    const chrome = borderBox ? paddingY + borderY : 0;

    node.style.height = 'auto';
    const content = borderBox ? node.scrollHeight + borderY : node.scrollHeight - paddingY;
    const min = lineHeight * minRows + chrome;
    const max = lineHeight * Math.max(maxRows, minRows) + chrome;

    node.style.height = `${Math.ceil(Math.min(Math.max(content, min), max))}px`;
    node.style.overflowY = content > max ? 'auto' : 'hidden';
    sized.current = true;
  }, [minRows, maxRows]);

  // A controlled value rewritten from outside arrives as a plain re-render with
  // no input event, so measure after every render rather than only the ones we
  // can observe. Layout effect, so the height is right before paint.
  useLayoutEffect(resize);

  // A table column moves when its neighbours' content changes, and a textarea
  // sized once is wrong from that moment on.
  useEffect(() => {
    const node = el.current;
    if (!node || minRows === undefined) return;
    if (typeof ResizeObserver === 'undefined') return;

    let lastWidth = node.clientWidth;
    const observer = new ResizeObserver(() => {
      const width = node.clientWidth;
      // Our own height write is also a resize; reacting to it would loop.
      if (width === lastWidth) return;
      lastWidth = width;
      resize();
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [minRows, resize]);

  const setRef = useCallback(
    (node: HTMLTextAreaElement | null) => {
      el.current = node;
      if (typeof ref === 'function') {
        const cleanup = ref(node);
        return () => {
          el.current = null;
          if (typeof cleanup === 'function') cleanup();
          else ref(null);
        };
      }
      if (ref) ref.current = node;
      return () => {
        el.current = null;
        if (ref) ref.current = null;
      };
    },
    [ref],
  );

  // Typing into an UNCONTROLLED textarea re-renders nothing, so the layout
  // effect above never runs for it.
  const handleInput = useCallback(
    (event: TextareaInputEvent) => {
      resize();
      onInput?.(event);
    },
    [resize, onInput],
  );

  return (
    <textarea
      // Passed straight through when autoGrow is omitted, so nothing about an
      // existing call site changes.
      ref={autoGrow ? setRef : ref}
      rows={rows ?? (autoGrow ? autoGrow.minRows : 3)}
      onInput={autoGrow ? handleInput : onInput}
      aria-invalid={invalid || undefined}
      className={cx(
        'w-full rounded-md border border-border bg-surface px-2.5 py-2 text-sm text-fg',
        'placeholder:text-fg-subtle',
        'transition-colors duration-150 ease-out motion-reduce:transition-none',
        'hover:border-hairline-strong',
        'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-border',
        'aria-invalid:border-danger',
        // A drag handle fights a height we rewrite on every render.
        autoGrow && 'resize-none',
        className,
      )}
      {...rest}
    />
  );
}
